// src/LayoutOperations.jsx
import React from "react";
import { Outlet, Navigate } from "react-router-dom";
import Header from "./components/ui/Header";
import { useAuth } from "./components/system/AppGate";
import OfflineBanner from "./components/offline/OfflineBanner";
import { useOfflineStatus } from "./hooks/useOfflineStatus";
import {
  APP_THEME_OPTIONS,
  APP_THEME_STORAGE_KEY,
  DEFAULT_APP_THEME,
  isValidAppTheme,
} from "./Layout";

/**
 * Layout exclusivo para Operaciones
 * - technician
 * - lab_manager
 * - admin
 * - NO permite flujo comercial
 */
export default function LayoutOperations() {
  const { role, loading } = useAuth();
  const offlineStatus = useOfflineStatus();
  const [appTheme, setAppTheme] = React.useState(DEFAULT_APP_THEME);

  React.useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(APP_THEME_STORAGE_KEY);
      if (isValidAppTheme(savedTheme)) {
        setAppTheme(savedTheme);
      }
    } catch {
      setAppTheme(DEFAULT_APP_THEME);
    }
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute("data-app-theme", appTheme);
    try {
      localStorage.setItem(APP_THEME_STORAGE_KEY, appTheme);
    } catch {
      // no-op
    }
  }, [appTheme]);

  const handleThemeChange = React.useCallback((nextTheme) => {
    if (!isValidAppTheme(nextTheme)) return;
    setAppTheme(nextTheme);
  }, []);

  // ⛔ Mientras se resuelve sesión / rol → NO renderizar nada (evita flash)
  if (loading) {
    return null;
  }

  // ⛔ Sin rol definido → no renderizar
  if (!role) {
    return <Navigate to="/login" replace />;
  }

  const allowedRoles = ["technician", "lab_manager", "admin"];

  // ⛔ Rol sin permisos de operaciones
  if (!allowedRoles.includes(role)) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen app-page-bg">
      {/* Header (solo se renderiza si el rol es válido) */}
      <Header
        appTheme={appTheme}
        appThemeOptions={APP_THEME_OPTIONS}
        onAppThemeChange={handleThemeChange}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <OfflineBanner status={offlineStatus} />
      </div>

      {/* Contenido de Operaciones */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 app-shell-bg rounded-xl">
        <Outlet />
      </main>
    </div>
  );
}
